<script setup lang="ts">
  import {
    CropperCanvas,
    CropperHandle,
    CropperImage,
  } from '@cropper/elements'
  import { ref, useTemplateRef, watch } from 'vue'
  import { useAppModal } from '@/components/modal'
  import { useImgCropper } from '@/functions/img'

  CropperCanvas.$define()
  CropperImage.$define()
  CropperHandle.$define()

  const props = defineProps<{
    aspectX: number,
    aspectY: number,
    title: string,
    initialCenterSize?: 'contain' | 'fill',
    src: string,
  }>()

  const model = defineModel<Blob | null>({ default: null })

  const refCropperCanvas = useTemplateRef('refCropperCanvas')
  const refCropperImage = useTemplateRef('refCropperImage')

  const {
    elImage,
    rotate,
    flipHorizontal,
    flipVertical,
    alignHorizontalLeft,
    alignHorizontalCenter,
    alignHorizontalRight,
    alignVerticalTop,
    alignVerticalMiddle,
    alignVerticalBottom,
    fitToHeight,
    fitToWidth,
    autoFitCover,
    autoFitContain,
    getResultBlob,
  } = useImgCropper(refCropperCanvas, refCropperImage)

  const saveCrop = async () => {
    model.value = await getResultBlob()
    data.value = elImage().$getTransform() // save the last state so that next reopen resume on this sate
    close()
  }

  const data = ref<number[]>([])
  const { modal, open, close } = useAppModal(() => {
    setTimeout(() => { // resume from the last state
      if (data.value.length) {
        elImage().$setTransform(data.value)
      }
    }, 50)
  })
  watch(() => props.src, () => {
    data.value = [] // reset data when img src is changed
  })

  defineExpose({ open })
</script>

<template>
  <app-modal
    ref="modal"
    :title="title">
    <cropper-canvas
      ref="refCropperCanvas"
      class="max-h-[calc(100vh-300px)] mx-auto"
      background
      :style="{ aspectRatio: `${aspectX}/${aspectY}` }">
      <cropper-image
        ref="refCropperImage"
        :src="src"
        alt="CropperJS"
        rotatable
        scalable
        initial-center-size="cover"
        translatable />
      <cropper-handle
        action="move"
        theme-color="rgba(255, 255, 255, 0)" />
    </cropper-canvas>

    <div class="my-5 space-y-4">
      <div class="flex-center">
        <app-button
          class="btn-gray-outline btn-icon mr-1"
          title="Rotate Left"
          @click="rotate(-90)">
          <fa-icon icon="i-far-rotate-left" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-4"
          title="Rotate Right"
          @click="rotate(90)">
          <fa-icon icon="i-far-rotate-right" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Flip Horizontal"
          @click="flipHorizontal">
          <fa-icon icon="i-far-arrow-right-arrow-left" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-4 rotate-90"
          title="Flip Vertical"
          @click="flipVertical">
          <fa-icon icon="i-far-arrow-right-arrow-left" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Fit To Width"
          @click="fitToWidth">
          <fa-icon icon="i-far-arrows-left-right-to-line" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-4 rotate-90"
          title="Fit To Height"
          @click="fitToHeight">
          <fa-icon icon="i-far-arrows-left-right-to-line" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Auto Fit (Contain)"
          @click="autoFitContain">
          <fa-icon icon="i-far-compress" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Auto Fit (Cover)"
          @click="autoFitCover">
          <fa-icon icon="i-far-expand" />
        </app-button>
      </div>

      <div class="flex-center">
        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Align Horizontal Left"
          @click="alignHorizontalLeft">
          <fa-icon icon="i-far-objects-align-left" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Align Horizontal Center"
          @click="alignHorizontalCenter">
          <fa-icon icon="i-far-objects-align-center-horizontal" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-4"
          title="Align Horizontal Right"
          @click="alignHorizontalRight">
          <fa-icon icon="i-far-objects-align-right" />
        </app-button>

        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Align Vertical Top"
          @click="alignVerticalTop">
          <fa-icon icon="i-far-objects-align-top" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline mr-1"
          title="Align Vertical Middle"
          @click="alignVerticalMiddle">
          <fa-icon icon="i-far-objects-align-center-vertical" />
        </app-button>
        <app-button
          class="btn-icon btn-gray-outline"
          title="Align Vertical Bottom"
          @click="alignVerticalBottom">
          <fa-icon icon="i-far-objects-align-bottom" />
        </app-button>
      </div>
    </div>

    <app-modal-footer>
      <app-button
        class="btn-primary"
        @click="saveCrop">
        Simpan
      </app-button>
      <app-button
        class="btn-gray-outline"
        @click="close">
        Batal
      </app-button>
    </app-modal-footer>
  </app-modal>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .btn-icon {
    @apply flex-shrink-0 !p-0 !text-xs !size-7 sm:!size-9;
  }
</style>
